// src/belediye/components/mesajlasma/WizardQuestion.js
// FAZ-42: Adım-adım SEÇENEKLİ-SORU paneli (AskUserQuestion tarzı).
// FAZ-E: Serbest metin (kind:"text" adımları) ve seçenekli adımların serbest-metin
// "custom" cevabı, panelin ALTINDAKİ MESAJ KUTUSU (.msj-composer görünümü) ile
// yazılır — ayrı "Tamam"/"İleri" düğmesi YOK, Gönder (uçak) ile ilerler; her
// adımda kendi placeholder'ı; WhatsApp-stili auto-grow. Seçenekler üstte kalır.
// (datetime picker gibi custom'lar `allowCustom` ile satır-içi kalır.)
// Domain-agnostik: her adım bir "patch" üretir; son adımda onComplete(birleşik).
import React, { useEffect, useRef, useState } from "react";
import {
  XMarkIcon,
  ChevronLeftIcon,
  ArrowRightIcon,
  PencilSquareIcon,
  PaperClipIcon,
} from "@heroicons/react/24/outline";

import SihirbazAltSatir from "./SihirbazAltSatir";

export default function WizardQuestion({
  steps = [],
  onComplete,
  onCancel,
  submitting = false,
  // FAZ-F: (opsiyonel) composer'daki ataç/foto düğmesi.
  // FAZ-74: artık TETİKLEYİCİ değil, CHANGE HANDLER'dır — düğme bir <label>
  // ve dosya girişi onun içinde. Gerekçe: `input.click()` ile açılan dosya
  // penceresini tarayıcı sessizce yutabiliyor (bkz. EkSecici'deki not).
  onAttachFiles,
  hasAttachment = false, // FAZ-F: ek (foto) varsa boş metinle de Gönder aktif
}) {
  const [idx, setIdx] = useState(0);
  const [acc, setAcc] = useState({});
  const [textVal, setTextVal] = useState("");
  const [customOpen, setCustomOpen] = useState(false);
  const [customVal, setCustomVal] = useState("");
  const composerRef = useRef(null);

  const step = steps[idx] || null;
  const total = steps.length;
  const isLast = idx === total - 1;

  // FAZ-E: bu adım alttaki mesaj-kutusu ile serbest metin alıyor mu?
  //  - kind:"text" → her zaman (placeholder/attach/maxLength step'ten).
  //  - kind:"options" + step.composer → seçeneklerin yanında serbest-metin cevabı.
  const composerCfg = !step
    ? null
    : step.kind === "text"
    ? {
        placeholder: step.placeholder,
        toPatch: (v) => ({ [step.key]: v.trim() }),
        attach: step.attach,
        maxLength: step.maxLength || 2000,
        required: step.required, // G13: zorunlu metin adımı (boş gönderilemez)
      }
    : step.composer || null;

  // FAZ-42 I1: adım değişince yerel girişleri o adımın BİRİKMİŞ değeriyle yeniden
  // doldur — "gördüğün gider" (geri gidince alan eski değerle dolar).
  const rehydrate = (nextAcc, nextIdx) => {
    const s = steps[nextIdx];
    setTextVal(s && s.kind === "text" ? (nextAcc[s.key] ?? "") : "");
    setCustomOpen(false);
    setCustomVal("");
  };

  const commit = (patch) => {
    if (submitting) return;
    const next = { ...acc, ...(patch || {}) };
    setAcc(next);
    if (isLast) {
      onComplete && onComplete(next);
    } else {
      const ni = idx + 1;
      setIdx(ni);
      rehydrate(next, ni);
    }
  };

  const back = () => {
    if (submitting) return;
    // FAZ-55/6+8: açık bir özel seçici (customPicker, ör. DuePicker) varsa GERİ
    // önce onu kapatır (adım değişmez). Böylece seçicilerin kendi "Vazgeç"
    // düğmesine gerek kalmaz — kullanıcı: "vazgeç ve ileri butonlarını kaldır."
    if (customOpen) {
      setCustomOpen(false);
      return;
    }
    if (idx === 0) {
      onCancel && onCancel();
      return;
    }
    const pi = idx - 1;
    setIdx(pi);
    rehydrate(acc, pi);
  };

  // FAZ-E: composer Gönder — geçerli adımın metnini işler. text adımda boş da
  // olabilir (boş "" gönderilir; Atla = key hiç eklenmez). Seçenekli-composer
  // adımda boş gönderim engellenir (aşağıda disabled).
  const submitComposer = () => {
    if (submitting || !composerCfg) return;
    // FAZ-F: boş seçenekli-composer'da (text adımı değil + ek yok) Enter de
    // göndermesin — Gönder düğmesindeki sendDisabled ile aynı kural.
    if (step.kind !== "text" && !textVal.trim() && !hasAttachment) return;
    // G13: zorunlu metin adımında boş gönderim engellenir (Enter dahil).
    if (composerCfg.required && !textVal.trim()) return;
    commit(composerCfg.toPatch(textVal));
  };

  // FAZ-E/R15: mesaj kutusu WhatsApp-stili yazdıkça büyür (max 108 = CSS ile aynı).
  // FAZ-83/3: BOŞKEN ÖLÇÜLMEZ — `scrollHeight` yer tutucu metnini de sayıyor;
  // uzun bir yer tutucu sarınca kutu yazı yokken bile aşağı doğru büyüyordu
  // (kullanıcı: "bu yazı gerçek bir yazıymış gibi mesaj kutusunu aşağı
  // sürüklüyor").
  useEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    if (!textVal) {
      el.style.height = "";
      return;
    }
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 108)}px`;
  }, [textVal, idx]);

  if (!step) return null;

  const sendDisabled =
    submitting ||
    (step.kind !== "text" && !textVal.trim() && !hasAttachment) ||
    // G13: zorunlu metin adımı boşken Gönder pasif.
    (!!composerCfg && composerCfg.required && !textVal.trim());

  return (
    <div className="msj-wq" role="group" aria-label="Adım sihirbazı">
      <div className="msj-wq-head">
        <button
          type="button"
          className="msj-wq-nav"
          onClick={back}
          aria-label={idx === 0 && !customOpen ? "Vazgeç" : "Geri"}
          disabled={submitting}
        >
          <ChevronLeftIcon />
        </button>
        <span className="msj-wq-title">{step.title}</span>
        <span className="msj-wq-count">
          {idx + 1}/{total}
        </span>
        <button
          type="button"
          className="msj-wq-close"
          onClick={onCancel}
          aria-label="Kapat"
          disabled={submitting}
        >
          <XMarkIcon />
        </button>
      </div>

      {step.kind === "options" && (
        <ul className="msj-wq-options">
          {step.options.map((o, i) => (
            <li key={i}>
              <button
                type="button"
                className="msj-wq-opt"
                disabled={submitting}
                // FAZ-55: `action`'lı seçenek ADIMI İLERLETMEZ — yan etkisini
                // çalıştırır (ör. dosya seçiciyi açar) ve kullanıcı aynı adımda
                // kalır. `patch`'li seçenek eski davranışıyla ilerletir.
                onClick={() => (o.action ? o.action() : commit(o.patch))}
              >
                <span className="msj-wq-num" aria-hidden="true">{i + 1}</span>
                <span className="msj-wq-lbl">
                  {o.label}
                  {o.hint && <span className="msj-wq-hint">{o.hint}</span>}
                </span>
                <ArrowRightIcon className="msj-wq-go" />
              </button>
            </li>
          ))}

          {/* FAZ-55: `customPicker` — özel seçim satır-içi BİLEŞENLE yapılır
              (ör. süre için DuePicker). allowCustom'ın tek-input'lu hâlinin
              yerine geçer; bileşen kendi Tamam/Vazgeç'ini taşır. */}
          {step.customPicker && (
            <li className="msj-wq-customwrap">
              {!customOpen ? (
                <button
                  type="button"
                  className="msj-wq-opt msj-wq-opt--custom"
                  disabled={submitting}
                  onClick={() => setCustomOpen(true)}
                >
                  <span className="msj-wq-num" aria-hidden="true">
                    <PencilSquareIcon />
                  </span>
                  <span className="msj-wq-lbl">{step.customPicker.label || "Diğer…"}</span>
                </button>
              ) : (
                step.customPicker.render({
                  onDone: (patch) => commit(patch),
                  onCancel: () => setCustomOpen(false),
                  // FAZ-55/8: "atla butonu hep aynı yerde olsun" — özel seçici
                  // açıkken de adımı atlamak mümkün; seçicinin kendi alt
                  // satırında, Gönder'in solunda.
                  onSkip: () => commit({}),
                })
              )}
            </li>
          )}

          {step.allowCustom && (
            <li className="msj-wq-customwrap">
              {!customOpen ? (
                <button
                  type="button"
                  className="msj-wq-opt msj-wq-opt--custom"
                  disabled={submitting}
                  onClick={() => setCustomOpen(true)}
                >
                  <span className="msj-wq-num" aria-hidden="true">
                    <PencilSquareIcon />
                  </span>
                  <span className="msj-wq-lbl">
                    {step.allowCustom.label || "Diğer…"}
                  </span>
                </button>
              ) : (
                <div className="msj-wq-custom-row">
                  <input
                    type={step.allowCustom.inputType || "text"}
                    className="msj-wq-input"
                    autoFocus
                    placeholder={step.allowCustom.placeholder || ""}
                    value={customVal}
                    onChange={(e) => setCustomVal(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && customVal) {
                        e.preventDefault();
                        commit(step.allowCustom.toPatch(customVal));
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="btn-md btn-md-primary"
                    disabled={submitting || !customVal}
                    onClick={() => commit(step.allowCustom.toPatch(customVal))}
                  >
                    Tamam
                  </button>
                </div>
              )}
            </li>
          )}

          {/* FAZ-55/10: özel seçici AÇIKKEN bu çıplak "Atla" çizilmez —
              seçicinin kendi onay satırında zaten bir atlama/vazgeçme düğmesi
              var ve ikisi alt alta düşüyordu (kullanıcı: "iki defa atla kısmı
              var"). Kapalıyken adımı atlamanın tek yolu burasıdır. */}
          {step.optional && !composerCfg && !customOpen && (
            <li>
              <button
                type="button"
                className="msj-wq-skip"
                onClick={() => commit({})}
                disabled={submitting}
              >
                Atla
              </button>
            </li>
          )}
        </ul>
      )}

      {/* FAZ-55/8 — TEK SEÇİCİLİ ADIM (kind:"picker").
          Kullanıcı akışı (2026-07-28): mahalle artık KENDİ adımı ve o adıma
          gelindiğinde seçici DOĞRUDAN açılır — önce "Mahalle seç" düğmesine
          basmak gereksiz bir tık. Seçici kendi mesaj kutusunu taşır; ilerletme
          onun GÖNDER düğmesi, atlama yanındaki ATLA. */}
      {step.kind === "picker" &&
        step.render({
          onDone: (patch) => commit(patch),
          onSkip: () => commit({}),
          acc,
        })}

      {composerCfg && (
        // FAZ-E: adımın serbest metni alttaki MESAJ KUTUSUYLA yazılır (.msj-composer
        // görünümü birebir) — Gönder (uçak) ile ilerler, ayrı "Tamam" yok.
        // FAZ-83/B1: satır ortak bileşenden — ATLA sol uçta, GÖNDER sağ uçta.
        <SihirbazAltSatir
          onSkip={step.optional ? () => commit({}) : undefined}
          skipTitle="Bu adımı boş geç"
          gonderAktif={!sendDisabled}
          onGonder={submitComposer}
          gonderTitle="Gönder"
          pasif={submitting}
        >
          {composerCfg.attach && onAttachFiles && (
            <label
              className="msj-iconbtn"
              title="Fotoğraf veya dosya ekle"
              aria-label="Fotoğraf veya dosya ekle"
            >
              {/* FAZ-78: mobilde `hidden` giriş dosya seçiciyi açmıyor — bkz. .msj-gizli-dosya */}
              <input type="file" className="msj-gizli-dosya" multiple onChange={onAttachFiles} />
              <PaperClipIcon />
            </label>
          )}
          <textarea
            ref={composerRef}
            rows={1}
            value={textVal}
            maxLength={composerCfg.maxLength || 2000}
            placeholder={composerCfg.placeholder || "Yazın…"}
            autoFocus
            onChange={(e) => setTextVal(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submitComposer();
              }
            }}
          />
        </SihirbazAltSatir>
      )}
    </div>
  );
}
